<template>
	<validation-provider
		#default="{ errors }"
		:name="fieldName || $props.label"
		:rules="rules"
		:mode="validationMode"
		:custom-messages="customMessages"
		:detect-input="validateField"
		tag="div"
		:class="tooltipText || reserveTooltipSpace ? 'd-flex flex-row cassie-horizontal-sm' : ''"
	>
		<div class="text-field-wrapper">
			<v-text-field
				v-bind="$props"
				:readonly="$props.readonly || viewOnly"
				:error-messages="errors.length ? [errors[0]] : []"
				:rules="[]"
				:hide-details="!hasRules || !errors.length"
				:class="[small ? 'cassie-text-field--small' : '', getQaSelectorClass($props)]"
				dense
				:data-testid="testId"
				class="cassie-text-field"
				v-on="$listeners"
			>
				<template
					v-if="$slots.append"
					#append
				>
					<slot name="append" />
				</template>
			</v-text-field>
			<div
				v-if="explainer"
				class="text-field-explainer"
			>
				{{ explainer }}
			</div>
		</div>
		<v-tooltip
			v-if="tooltipText"
			right
		>
			<template #activator="{ on }">
				<v-icon
					light
					v-on="on"
				>
					mdi-information
				</v-icon>
			</template>
			<div v-dompurify-html="tooltipText" />
		</v-tooltip>
		<div
			v-if="reserveTooltipSpace"
			style="width: 24px;"
		/>
	</validation-provider>
</template>

<script>
import { VTextField } from 'vuetify/lib'
import FieldValidationMixin from '../validation/field-validation-mixin.js'

export default {
	name: 'text-field',
	extends: VTextField,
	mixins: [FieldValidationMixin],
	props: {
		viewOnly: {
			type: Boolean,
			default: false
		},
		light: {
			type: Boolean,
			default: undefined // Let text-field inherit theme from app (was: true)
		},
		outlined: {
			type: Boolean,
			default: true
		},
		small: {
			type: Boolean,
			default: false
		},
		tooltipText: {
			type: String
		},
		reserveTooltipSpace: {
			type: Boolean,
			default: false
		},
		testId: {
			type: String,
			default: 'cassie_text_field'
		},
		explainer: {
			type: String
		}
	},
	methods: {
		getQaSelectorClass (props) {
			let trimmedLabel = props.label
			if (trimmedLabel) {
				if (trimmedLabel.includes(' *')) {
					trimmedLabel = trimmedLabel.replace(' *', '')
				}
				trimmedLabel = trimmedLabel.replace(/\s/g, '-').toLowerCase()
			}
			return `qa-textfield-${trimmedLabel}`
		}
	}
}
</script>

<style lang="scss">
.text-field-wrapper {
	flex: 1;
}

.text-field-explainer {
	font-size: 0.75rem;
	font-family: 'Inter', sans-serif;
	color: var(--global-muted-foreground);
	margin-top: 4px;
	line-height: 1.4;
}

.cassie-text-field {
	// Label and input font styling - aligned with NextJS (text-sm = 0.875rem, font-medium = 500)
	// Note: Global styles in app.vue also apply these, but component-level provides specificity
	.v-input__control .v-input__slot .v-text-field__slot .v-label {
		font-size: 0.875rem !important;
		font-family: 'Inter', sans-serif;
		font-weight: 500;
	}

	.v-input__control .v-input__slot .v-text-field__slot input {
		font-size: 0.875rem !important;
		font-family: 'Inter', sans-serif;
		font-weight: 400;
	}

	// Placeholder text
	.v-input__control .v-input__slot .v-text-field__slot input::placeholder {
		font-size: 0.875rem !important;
		font-family: 'Inter', sans-serif;
	}
	// Change focus/active outline color from primary to syrenis-black
	&.v-input--is-focused .v-input__slot {
		border-color: var(--global-syrenis-black) !important;
	}

	&.v-input--is-focused:not(.v-input--has-state) .v-label {
		color: var(--global-syrenis-black) !important;
	}

	// Change cursor color to syrenis-black
	input {
		caret-color: var(--global-syrenis-black) !important;
	}

	// Change appended/prepended icon color to syrenis-black when focused
	&.v-input--is-focused {
		.v-input__append-inner .v-icon,
		.v-input__prepend-inner .v-icon {
			color: var(--global-syrenis-black) !important;
		}
	}

	.v-input__slot fieldset {
		border-color: inherit !important;
	}

	&--small {
		.v-input__control .v-input__slot {
			min-height: 0 !important;
			padding: 0 8px !important;
			display: flex !important;
			align-items: center !important;
		}

		.v-input__control .v-input__slot .v-input__append-inner {
			margin-top: 4px !important;
		}

		.v-input__control .v-input__slot label {
			margin-top: -12px !important;
		}

		.v-input__control .v-input__slot label.v-label--active {
			margin: 0 0 0 5px !important;
		}

		.v-text-field__details {
			margin: 2px !important;
		}

		.v-label {
			top: 18px !important;
			font-size: 14px !important;
		}

		input {
			font-size: 85%;
		}

		.v-label--active {
			-webkit-transform: translateY(-25px) scale(0.65) !important;
			transform: translateY(-25px) scale(0.65) !important;
		}
	}
	// Input background - white in light mode, dark background in dark mode
	.v-input__slot {
		background-color: var(--global-card-background) !important;
	}

	// Placeholder text styling
	input::placeholder {
		color: var(--global-muted-foreground);
		opacity: 0.7;
	}
}
</style>
